/**
 * PHOTOS — envoi et affichage.
 * `Galerie` : gestion côté pro (envoi, légende, suppression).
 * `Vitrine` : lecture seule, pour la page publique.
 * L'URL renvoyée par l'API est relative : on la préfixe par l'hôte du backend.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Loader2, Trash2, X } from "lucide-react";
import { api, messageErreur } from "../api";
import { Micro } from "./Bits";

const HOTE = process.env.REACT_APP_BACKEND_URL;

export const urlPhoto = (url) => `${HOTE}${url}`;

export function Galerie({ clientId, portfolio = false, titre, aide }) {
  const [liste, setListe] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const [zoom, setZoom] = useState(null);
  const input = useRef(null);

  const charger = useCallback(async () => {
    try {
      const { data } = await api.get("/photos", {
        params: portfolio ? { portfolio: true } : { client_id: clientId },
      });
      setListe(data);
    } catch {
      setListe([]);
    }
  }, [clientId, portfolio]);

  useEffect(() => {
    charger();
  }, [charger]);

  const envoyer = async (fichiers) => {
    const fs = Array.from(fichiers || []).slice(0, 10);
    if (!fs.length) return;
    setEnvoi(true);
    let ok = 0;
    for (const f of fs) {
      const fd = new FormData();
      fd.append("fichier", f);
      if (portfolio) fd.append("portfolio", "true");
      if (clientId) fd.append("client_id", clientId);
      try {
        await api.post("/photos", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 90000,
        });
        ok += 1;
      } catch (e) {
        toast.error(messageErreur(e, "Envoi impossible."));
      }
    }
    setEnvoi(false);
    if (ok) toast.success(ok > 1 ? `${ok} photos ajoutées.` : "Photo ajoutée.");
    charger();
  };

  const supprimer = async (id) => {
    setListe((l) => l.filter((p) => p.id !== id));
    try {
      await api.delete(`/photos/${id}`);
    } catch (e) {
      toast.error(messageErreur(e));
      charger();
    }
  };

  return (
    <div data-testid={portfolio ? "galerie-portfolio" : "galerie-client"}>
      {titre && (
        <div className="flex items-baseline justify-between pb-2.5 mb-4" style={{ borderBottom: "1px solid var(--border)" }}>
          <Micro>{titre}</Micro>
          {liste && <span className="num text-[12px]" style={{ color: "var(--text-subtle)" }}>{liste.length}</span>}
        </div>
      )}
      {aide && (
        <p className="text-[13px] mb-4" style={{ color: "var(--text-muted)" }}>
          {aide}
        </p>
      )}

      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
        <button
          onClick={() => input.current?.click()}
          data-testid={portfolio ? "btn-ajouter-photo-portfolio" : "btn-ajouter-photo-client"}
          className="aspect-square flex flex-col items-center justify-center gap-1.5"
          style={{
            borderRadius: "var(--radius-lg)",
            border: "1px dashed var(--border-strong)",
            background: "var(--surface)",
            color: "var(--text-muted)",
          }}
        >
          {envoi ? (
            <Loader2 size={20} strokeWidth={1.8} className="animate-spin" />
          ) : (
            <ImagePlus size={20} strokeWidth={1.7} />
          )}
          <span className="text-[11.5px] font-semibold">{envoi ? "Envoi…" : "Ajouter"}</span>
        </button>

        <input
          ref={input}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic"
          multiple
          hidden
          data-testid="input-photo"
          onChange={(e) => {
            envoyer(e.target.files);
            e.target.value = "";
          }}
        />

        {(liste || []).map((p) => (
          <div key={p.id} className="relative group">
            <button
              onClick={() => setZoom(p)}
              data-testid={`photo-${p.id}`}
              className="block w-full aspect-square overflow-hidden"
              style={{ borderRadius: "var(--radius-lg)", border: "1px solid var(--border)" }}
            >
              <img
                src={urlPhoto(p.url)}
                alt={p.legende || "Photo"}
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </button>
            <button
              onClick={() => supprimer(p.id)}
              data-testid={`supprimer-photo-${p.id}`}
              aria-label="Supprimer la photo"
              className="absolute top-1.5 right-1.5 h-8 w-8 inline-flex items-center justify-center"
              style={{
                borderRadius: "var(--radius-full)",
                background: "rgba(20,20,19,0.62)",
                color: "#fff",
                backdropFilter: "blur(6px)",
              }}
            >
              <Trash2 size={14} strokeWidth={1.9} />
            </button>
          </div>
        ))}
      </div>

      {zoom && (
        <div
          onClick={() => setZoom(null)}
          className="fixed inset-0 z-50 flex items-center justify-center p-5"
          style={{ background: "rgba(20,20,19,0.86)" }}
          data-testid="photo-zoom"
        >
          <img
            src={urlPhoto(zoom.url)}
            alt={zoom.legende || "Photo"}
            className="max-h-[86vh] max-w-full"
            style={{ borderRadius: "var(--radius-lg)" }}
          />
          <button
            aria-label="Fermer"
            className="absolute top-4 right-4 h-11 w-11 inline-flex items-center justify-center"
            style={{ borderRadius: "var(--radius-full)", background: "rgba(255,255,255,0.14)", color: "#fff" }}
          >
            <X size={20} strokeWidth={1.9} />
          </button>
        </div>
      )}
    </div>
  );
}

/** Vitrine publique : bande défilante, plein cadre au clic. */
export function Vitrine({ photos }) {
  const [zoom, setZoom] = useState(null);
  if (!photos?.length) return null;
  return (
    <div data-testid="vitrine-portfolio">
      <div className="flex gap-2.5 overflow-x-auto no-scrollbar -mx-4 px-4 pb-1">
        {photos.map((p) => (
          <button
            key={p.id}
            onClick={() => setZoom(p)}
            data-testid={`vitrine-photo-${p.id}`}
            className="shrink-0 overflow-hidden"
            style={{
              width: 156,
              height: 196,
              borderRadius: "var(--radius-lg)",
              border: "1px solid var(--border)",
            }}
          >
            <img
              src={urlPhoto(p.url)}
              alt={p.legende || "Réalisation"}
              loading="lazy"
              className="h-full w-full object-cover"
            />
          </button>
        ))}
      </div>

      {zoom && (
        <div
          onClick={() => setZoom(null)}
          className="fixed inset-0 z-50 flex items-center justify-center p-5"
          style={{ background: "rgba(20,20,19,0.88)" }}
          data-testid="vitrine-zoom"
        >
          <img
            src={urlPhoto(zoom.url)}
            alt={zoom.legende || "Réalisation"}
            className="max-h-[86vh] max-w-full"
            style={{ borderRadius: "var(--radius-lg)" }}
          />
        </div>
      )}
    </div>
  );
}
